/**
 * Natural Gas Tariff Converter
 *
 * Converts gas tariffs from user-friendly therms to solver-compatible MMBtu
 * and provides tiered cost calculation.
 */

const THERMS_PER_MMBTU = 10; // 1 MMBtu = 10 therms

/**
 * Convert gas tariff from therms (UI) to MMBtu (solver)
 * Handles both new split rates (delivery + commodity) and legacy single rate
 * @param {Object} gasTariff - Tariff object with rate_data in therms
 * @returns {Object} Converted tariff with rates in MMBtu
 */
export function convertGasTariffForSolver(gasTariff) {
    const { rate_data } = gasTariff;

    return {
        tariff_type: 'natural_gas',
        customer_charge_per_day: rate_data.customer_charge_per_day || 0,
        tiers: rate_data.tiers.map(tier => {
            // Handle both new split rates and legacy single rate
            const hasSplitRates = tier.delivery_rate_per_therm !== undefined;

            return {
                tier_number: tier.tier_number,
                max_usage_mmbtu: tier.max_usage_therms
                    ? tier.max_usage_therms / THERMS_PER_MMBTU
                    : null,
                delivery_rate_per_mmbtu: hasSplitRates
                    ? tier.delivery_rate_per_therm * THERMS_PER_MMBTU
                    : null,
                commodity_rate_per_mmbtu: hasSplitRates
                    ? tier.commodity_rate_per_therm * THERMS_PER_MMBTU
                    : null,
                rate_per_mmbtu: hasSplitRates
                    ? (tier.delivery_rate_per_therm + tier.commodity_rate_per_therm) * THERMS_PER_MMBTU
                    : tier.rate_per_therm * THERMS_PER_MMBTU
            };
        })
    };
}

/**
 * Calculate tiered gas cost for given usage
 * @param {number} usageMmbtu - Usage in MMBtu
 * @param {Object} convertedTariff - Tariff converted to MMBtu rates
 * @returns {number} Total cost including customer charge
 */
export function calculateTieredGasCost(usageMmbtu, convertedTariff) {
    let totalCost = 0;
    let usageConsumed = 0;

    // Calculate usage costs by tier
    // Note: tier max_usage_mmbtu represents cumulative boundaries, not individual tier widths
    for (let i = 0; i < convertedTariff.tiers.length; i++) {
        const tier = convertedTariff.tiers[i];
        const previousTierMax = i > 0 ? convertedTariff.tiers[i - 1].max_usage_mmbtu : 0;

        if (usageConsumed >= usageMmbtu) break;

        // Determine how much usage falls in this tier
        let tierUsage;
        if (tier.max_usage_mmbtu === null) {
            // Unlimited tier - use all remaining usage
            tierUsage = usageMmbtu - usageConsumed;
        } else {
            // Limited tier - calculate the tier width and how much we consume
            const tierWidth = tier.max_usage_mmbtu - previousTierMax;
            const remainingUsage = usageMmbtu - usageConsumed;
            tierUsage = Math.min(remainingUsage, tierWidth);
        }

        totalCost += tierUsage * tier.rate_per_mmbtu;
        usageConsumed += tierUsage;
    }

    // Add monthly customer charge (30.5 days average)
    totalCost += (convertedTariff.customer_charge_per_day || 0) * 30.5;

    return totalCost;
}

/**
 * Calculate tiered gas cost breakdown with separate delivery and commodity costs
 * @param {number} usageMmbtu - Usage in MMBtu
 * @param {Object} convertedTariff - Tariff converted to MMBtu rates
 * @returns {Object} Cost breakdown { deliveryCost, commodityCost, totalCost, customerCharge }
 */
export function calculateTieredGasCostBreakdown(usageMmbtu, convertedTariff) {
    let deliveryCost = 0;
    let commodityCost = 0;
    let usageConsumed = 0;

    // Calculate usage costs by tier
    // Note: tier max_usage_mmbtu represents cumulative boundaries, not individual tier widths
    for (let i = 0; i < convertedTariff.tiers.length; i++) {
        const tier = convertedTariff.tiers[i];
        const previousTierMax = i > 0 ? convertedTariff.tiers[i - 1].max_usage_mmbtu : 0;

        if (usageConsumed >= usageMmbtu) break;

        // Determine how much usage falls in this tier
        let tierUsage;
        if (tier.max_usage_mmbtu === null) {
            // Unlimited tier - use all remaining usage
            tierUsage = usageMmbtu - usageConsumed;
        } else {
            // Limited tier - calculate the tier width and how much we consume
            const tierWidth = tier.max_usage_mmbtu - previousTierMax;
            const remainingUsage = usageMmbtu - usageConsumed;
            tierUsage = Math.min(remainingUsage, tierWidth);
        }

        // Check if split rates are available
        const hasSplitRates = tier.delivery_rate_per_mmbtu !== null && tier.delivery_rate_per_mmbtu !== undefined;

        if (hasSplitRates) {
            deliveryCost += tierUsage * tier.delivery_rate_per_mmbtu;
            commodityCost += tierUsage * tier.commodity_rate_per_mmbtu;
        } else {
            // Legacy single rate - attribute all to commodity (or could split evenly)
            commodityCost += tierUsage * tier.rate_per_mmbtu;
        }

        usageConsumed += tierUsage;
    }

    // Calculate monthly customer charge (30.5 days average)
    const customerCharge = (convertedTariff.customer_charge_per_day || 0) * 30.5;

    // Total cost is the sum of all components
    const totalCost = deliveryCost + commodityCost + customerCharge;

    return {
        deliveryCost,
        commodityCost,
        totalCost,
        customerCharge
    };
}

/**
 * Convert usage from MMBtu to therms for display
 * @param {number} mmbtu - Usage in MMBtu
 * @returns {number} Usage in therms
 */
export function mmbtuToTherms(mmbtu) {
    return mmbtu * THERMS_PER_MMBTU;
}

/**
 * Convert usage from therms to MMBtu
 * @param {number} therms - Usage in therms
 * @returns {number} Usage in MMBtu
 */
export function thermsToMmbtu(therms) {
    return therms / THERMS_PER_MMBTU;
}
